import React, { useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
  Trophy,
  Star,
  Award,
  Crown,
  Zap,
  Compass,
  CheckSquare,
  Target,
  Search,
  Keyboard,
  Moon,
  Filter,
  Footprints,
  X,
} from 'lucide-react';

// Icon mapping
const ICON_MAP = {
  Trophy,
  Star,
  Award,
  Crown,
  Zap,
  Compass,
  CheckSquare,
  Target,
  Search,
  Keyboard,
  Moon,
  Filter,
  Footprints,
};

/**
 * AchievementNotification - Toast notification for unlocked achievements
 *
 * Shows a celebratory notification when the user unlocks an achievement.
 */
const AchievementNotification = ({ achievement, onDismiss, autoClose = 5000 }) => {
  const [visible, setVisible] = useState(false);
  const [closing, setClosing] = useState(false);

  const IconComponent = ICON_MAP[achievement.icon] || Trophy;

  const handleClose = useCallback(() => {
    setClosing(true);
    setTimeout(() => {
      onDismiss?.(achievement.id);
    }, 300);
  }, [onDismiss, achievement.id]);

  useEffect(() => {
    // Animate in
    const showTimer = setTimeout(() => setVisible(true), 50);

    // Auto close
    let closeTimer;
    if (autoClose > 0) {
      closeTimer = setTimeout(() => handleClose(), autoClose);
    }

    return () => {
      clearTimeout(showTimer);
      if (closeTimer) clearTimeout(closeTimer);
    };
  }, [autoClose, handleClose]);

  const content = (
    <div
      className={`fixed bottom-4 right-4 z-[200] transition-all duration-300 ${
        visible && !closing
          ? 'translate-y-0 opacity-100'
          : 'translate-y-4 opacity-0'
      }`}
    >
      <div className="relative overflow-hidden bg-gradient-to-r from-amber-500 via-yellow-500 to-amber-500 rounded-2xl shadow-2xl p-1">
        {/* Shimmer effect */}
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-shimmer" />

        <div className="relative bg-white dark:bg-slate-900 rounded-xl p-4">
          <button
            onClick={handleClose}
            className="absolute top-2 right-2 p-1 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>

          <div className="flex items-start gap-4 pr-6">
            {/* Icon with glow */}
            <div className="relative">
              <div className="absolute inset-0 bg-amber-400 rounded-full blur-lg opacity-50 animate-pulse" />
              <div className="relative p-3 rounded-full bg-gradient-to-br from-amber-400 to-yellow-500 text-white shadow-lg">
                <IconComponent className="w-8 h-8" />
              </div>
            </div>

            {/* Content */}
            <div className="flex-1">
              <p className="text-xs font-semibold uppercase tracking-wide text-amber-600 dark:text-amber-400">
                Achievement Unlocked!
              </p>
              <h4 className="text-lg font-bold text-slate-800 dark:text-slate-100 mt-0.5">
                {achievement.title}
              </h4>
              <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
                {achievement.description}
              </p>
              <div className="flex items-center gap-2 mt-2">
                <div className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 text-xs font-semibold">
                  <Star className="w-3 h-3" />
                  +{achievement.points} points
                </div>
              </div>
            </div>
          </div>

          {/* Confetti particles */}
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            {[...Array(12)].map((_, i) => (
              <div
                key={i}
                className="absolute w-2 h-2 rounded-full animate-confetti"
                style={{
                  left: `${10 + (i * 7)}%`,
                  backgroundColor: ['#fbbf24', '#f59e0b', '#d97706', '#22c55e', '#3b82f6', '#8b5cf6'][i % 6],
                  animationDelay: `${i * 0.1}s`,
                }}
              />
            ))}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        .animate-shimmer {
          animation: shimmer 2s infinite;
        }
        @keyframes confetti {
          0% {
            transform: translateY(0) rotate(0deg);
            opacity: 1;
          }
          100% {
            transform: translateY(-100px) rotate(720deg);
            opacity: 0;
          }
        }
        .animate-confetti {
          animation: confetti 1s ease-out forwards;
        }
      `}</style>
    </div>
  );

  return createPortal(content, document.body);
};

/**
 * AchievementNotificationStack - Manages multiple achievement notifications
 */
export const AchievementNotificationStack = ({ notifications, onDismiss }) => {
  return (
    <>
      {notifications.map((achievement, index) => (
        <div
          key={achievement.id}
          style={{ transform: `translateY(-${index * 10}px)` }}
        >
          <AchievementNotification
            achievement={achievement}
            onDismiss={onDismiss}
            autoClose={5000 + (index * 1000)}
          />
        </div>
      ))}
    </>
  );
};

export default AchievementNotification;
